import { useState, type FormEvent, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { hasAtLeastRole } from '@kn/shared';
import { api } from '../../api/client';
import { messageErreur } from '../../api/error-message';
import { useAuth } from '../../auth/auth-context';

const CLE = ['comment-types'] as const;

/**
 * Types de commentaire d'instruction (règle 14 v11.2).
 *
 * Trois types sont livrés — « Résolu », « En cours », « GDS » — mais la liste
 * appartient à KN : elle se complète et se renomme ici, sans livraison.
 *
 * **Désactiver n'efface rien.** Un type retiré disparaît de la liste de saisie ;
 * les commentaires déjà écrits gardent le leur. Sinon un dossier relu six mois
 * plus tard ne dirait plus ce qu'il disait — et c'est précisément quand on le
 * relit qu'on a besoin qu'il le dise.
 */
export function CommentTypesPage(): ReactElement {
  const { user } = useAuth();
  const peutModifier = user ? hasAtLeastRole(user.role, 'ADMIN') : false;
  const queryClient = useQueryClient();
  const [libelle, setLibelle] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const types = useQuery({ queryKey: CLE, queryFn: () => api.commentTypes(false) });

  const rafraichir = (texte: string): void => {
    setMessage(texte);
    setErreur(null);
    void queryClient.invalidateQueries({ queryKey: CLE });
  };

  const creation = useMutation({
    mutationFn: (valeur: string) => api.createCommentType({ libelle: valeur }),
    onSuccess: (type) => {
      setLibelle('');
      rafraichir(`Type « ${type.libelle} » créé.`);
    },
    onError: (error: unknown) => setErreur(messageErreur(error)),
  });

  const modification = useMutation({
    mutationFn: ({ id, body }: { id: string; body: { libelle?: string; actif?: boolean } }) =>
      api.updateCommentType(id, body),
    onSuccess: (type) =>
      rafraichir(
        type.actif
          ? `Type « ${type.libelle} » remis dans la liste de saisie.`
          : `Type « ${type.libelle} » retiré de la liste de saisie ; ses ${type.utilisations ?? 0} commentaire(s) sont conservés.`,
      ),
    onError: (error: unknown) => setErreur(messageErreur(error)),
  });

  function creer(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (libelle.trim() === '') {
      setErreur('Le libellé ne peut pas être vide.');
      return;
    }
    creation.mutate(libelle.trim());
  }

  const enCours = creation.isPending || modification.isPending;

  return (
    <>
      <h1>Référentiels &mdash; Types de commentaire</h1>
      <p className="sous-titre">
        Les étiquettes posées sur les commentaires d&apos;instruction. Elles n&apos;ont{' '}
        <strong>aucun effet</strong> sur l&apos;état d&apos;un écart : un commentaire « Résolu » ne
        masque rien.
      </p>

      {message && (
        <p className="alerte alerte--info" role="status">
          {message}
        </p>
      )}
      {erreur && (
        <p className="alerte alerte--erreur" role="alert">
          {erreur}
        </p>
      )}

      {peutModifier && (
        <div className="carte">
          <form className="barre-actions" onSubmit={creer}>
            <label className="champ champ--large" htmlFor="type-libelle">
              <span>Nouveau type</span>
              <input
                id="type-libelle"
                type="text"
                maxLength={60}
                placeholder="Par exemple : Litige transporteur"
                value={libelle}
                onChange={(event) => setLibelle(event.target.value)}
              />
            </label>
            <button type="submit" className="bouton" disabled={enCours}>
              Créer
            </button>
          </form>
        </div>
      )}

      <div className="carte">
        {types.isPending && <p className="chargement">Chargement des types…</p>}
        {types.isError && (
          <p className="alerte alerte--erreur" role="alert">
            {messageErreur(types.error)}
          </p>
        )}

        {types.data && (
          <table className="tableau">
            <thead>
              <tr>
                <th scope="col">Libellé</th>
                <th scope="col">Dans la liste de saisie</th>
                <th scope="col">Commentaires écrits</th>
                {peutModifier && <th scope="col">Action</th>}
              </tr>
            </thead>
            <tbody>
              {types.data.map((type) => (
                <tr key={type.id}>
                  <td>{type.libelle}</td>
                  <td>
                    <span
                      className={`etiquette ${type.actif ? 'etiquette--actif' : 'etiquette--inactif'}`}
                    >
                      {type.actif ? 'Proposé' : 'Retiré'}
                    </span>
                  </td>
                  <td className="droite">{(type.utilisations ?? 0).toLocaleString('fr-FR')}</td>
                  {peutModifier && (
                    <td>
                      <button
                        type="button"
                        className="bouton bouton--lien"
                        disabled={enCours}
                        onClick={() =>
                          modification.mutate({ id: type.id, body: { actif: !type.actif } })
                        }
                      >
                        {type.actif ? 'Retirer de la saisie' : 'Remettre dans la saisie'}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
